import { Injectable } from '@angular/core';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../../backend/amplify/data/resource';

export type GlobalSearchKind = 'lease' | 'owner' | 'tenant' | 'property';

/**
 * Une ligne de `globalSearch` — la palette de commande (Cmd+K).
 *
 * Les quatre familles partagent la même forme : la requête compose elle-même
 * le libellé (locataires du bail, nom du propriétaire, rue du bien…), l'écran
 * n'a qu'à l'afficher.
 */
export interface GlobalSearchHit {
  kind: GlobalSearchKind;
  id: string;
  label: string;
  sublabel: string | null;
  /** Bail terminé (départ effectif, à défaut fin contractuelle, passé). Toujours faux hors baux. */
  ended: boolean;
}

/** Longueur minimale de la saisie — la requête ne rend rien en dessous. */
export const GLOBAL_SEARCH_MIN_LENGTH = 2;

@Injectable({ providedIn: 'root' })
export class GlobalSearchService {
  private client = generateClient<Schema>();

  /**
   * Baux, propriétaires, locataires et biens correspondant à `query`, 6 au plus
   * par famille, dans l'ordre baux → propriétaires → locataires → biens.
   * Insensible aux accents (migration 0105). Voir `globalSearch.sql`.
   */
  async search(query: string): Promise<GlobalSearchHit[]> {
    const searchQuery = query.trim();
    if (searchQuery.length < GLOBAL_SEARCH_MIN_LENGTH) return [];

    // Le client Amplify ne lève PAS sur une erreur GraphQL : il renvoie
    // { data: null, errors }. Sans ce test, une requête pas encore déployée
    // se lirait « aucun résultat ».
    const result = await this.client.queries.globalSearch({ searchQuery });
    if (result?.errors?.length) {
      console.error('globalSearch GraphQL errors:', result.errors.map(e => e.message ?? e));
      throw new Error('Failed to run global search');
    }

    const raw = typeof result?.data === 'string' ? JSON.parse(result.data) : result?.data;
    if (!Array.isArray(raw)) return [];

    return raw.map((row: any): GlobalSearchHit => ({
      kind: row.kind,
      id: row.id,
      label: row.label ?? '',
      sublabel: row.sublabel ?? null,
      // La Lambda SQL peut rendre un booléen Postgres en texte.
      ended: row.ended === true || row.ended === 't' || row.ended === 'true',
    }));
  }
}
